import React from 'react';
import { useCartContext } from './context/CartContext';
import { Portal } from '../../components/portal/Portal';
import { CartItemChangeAlert } from '@jbhi-fi/components/src/Cart/CartItemChangeAlert';
import { resetLineItemAddToCartProperties } from './resetLineItemAddToCartProperties';
import { useSellerUrlHandle } from '@jbhi-fi/components/src/hooks/useSellerUrlHandle/useSellerUrlHandle';
import { drawCart, setCartLoading } from '../legacyCart';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { ShopifyStorefrontClient } from '../../api/shopify/ShopifyStorefrontClient';
import { transformStorefrontCartToJbCart } from './transformStorefrontCartToJbCart';
import { JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';

export type CartItemAlertProps = {
  isCartPage: boolean;
};

const storefrontClient = new ShopifyStorefrontClient();

const getHasSellerChanged = (item: JbCartItem) => {
  const { _marketplaceSellerId: currentSellerId, _marketplaceSellerIdAtc: addedToCartSellerId } =
    item.properties as JbCartItem['properties'];
  return !!addedToCartSellerId && addedToCartSellerId !== currentSellerId;
};

const handleOnDismiss = async (item: JbCartItem) => {
  if (item.line_id) {
    setCartLoading(true);
    const newProperties = resetLineItemAddToCartProperties(item);
    const updatedCart = await storefrontClient.updateCartLine(
      item.line_id,
      item.quantity,
      filterAndMapAttributes(newProperties)
    );
    await drawCart(transformStorefrontCartToJbCart(updatedCart));
  }
};

export const CartItemAlerts = ({ isCartPage }: CartItemAlertProps) => {
  const { cart } = useCartContext();

  const sellerIds: Array<string> = [
    ...new Set(
      cart?.items
        .filter(item => getHasSellerChanged(item))
        .map(item => item.properties?._marketplaceSellerId as string)
        .filter(Boolean)
    ),
  ];

  const sellerHandles = useSellerUrlHandle({ storefrontClient, sellerIds })
    .filter(r => !r.isLoading && !r.error && r.data)
    .map(result => result.data!);

  return (
    <>
      {cart?.items.map((item, index) => {
        const { final_line_price: currentPrice, quantity } = item;
        const {
          _priceAtc: addedToCartPrice,
          _marketplaceSeller: currentSellerName,
          _marketplaceSellerId: currentSellerId,
        } = item.properties as JbCartItem['properties'];

        const hasPriceIncreased = !!addedToCartPrice && currentPrice > Number(addedToCartPrice) * quantity;
        const currentSellerUrl = sellerHandles.find(seller => seller.id === currentSellerId)?.handle || '';

        const container = document.querySelector<HTMLDivElement>(
          `.cart-row-item[data-sku="${item.sku}"][data-index="${index}"] .cart-item__alert`
        );
        if (!container) return;
        const hasSellerChanged = getHasSellerChanged(item);

        return (
          <Portal container={container} key={index}>
            {hasSellerChanged || hasPriceIncreased ? (
              <CartItemChangeAlert
                isCartPage={isCartPage}
                variant={hasSellerChanged ? 'seller' : 'price'}
                priceVariant={hasPriceIncreased ? 'increase' : undefined}
                currentSellerName={currentSellerName || ''}
                currentSellerHref={currentSellerUrl ? `/collections/${currentSellerUrl}` : ''}
                handleOnDismiss={() => handleOnDismiss(item)}
              />
            ) : null}
          </Portal>
        );
      })}
    </>
  );
};
